/**
 * SiteAnalytics — first-party, cookie-based visitor tracking for public pages.
 * Captures pageviews, engagement (time-on-page + scroll depth) and clicks, then
 * beacons them to /api/analytics/collect. Mounted once inside PublicLayout.
 *
 * Consent gating: this first-party, IP-light traffic measurement follows the
 * shared consent store (localStorage 'corvana_consent'). If the visitor turns
 * the Analytics category OFF (Cookie banner → Necessary only / toggle), tracking
 * stops and the cv_vid cookie is cleared. Before any explicit choice we use
 * implied consent (AU) and measure traffic; the banner lets visitors opt out.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const EXCLUDE = ['/app', '/corvana-crm', '/crm'];

function consentGranted() {
  try {
    if (window.self !== window.top) return false; // never track inside an embed/preview
    if (window.__cvConsent === 'denied') return false;
    const raw = localStorage.getItem('corvana_consent');
    if (raw) {
      // Explicit choice made — honour the Analytics category.
      try { return JSON.parse(raw).analytics === true; } catch { return false; }
    }
    // No explicit choice yet — implied consent (AU); banner lets visitors opt out.
    return localStorage.getItem('cv_consent') !== 'denied';
  } catch {
    return true;
  }
}

function clearVidCookie() {
  try { document.cookie = 'cv_vid=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax'; } catch { /* ignore */ }
}

function getVid() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)cv_vid=([^;]+)/);
    if (m) return m[1];
    const vid = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    const exp = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString();
    document.cookie = `cv_vid=${vid}; expires=${exp}; path=/; SameSite=Lax`;
    return vid;
  } catch {
    return 'v_anon';
  }
}

function getSid() {
  try {
    let sid = sessionStorage.getItem('cv_sid');
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('cv_sid', sid);
    }
    return sid;
  } catch {
    return 's_anon';
  }
}

function device() {
  try {
    return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
}

function send(events) {
  if (!API || !events.length) return;
  const url = `${API}/api/analytics/collect`;
  const payload = JSON.stringify({ events });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
  } catch {
    /* fall through */
  }
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
}

export default function SiteAnalytics() {
  const location = useLocation();
  const path = location.pathname;
  const startRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const curPathRef = useRef(path);
  const flushedRef = useRef(false);

  const scrollPct = () => {
    try {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 100;
      return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    } catch {
      return 0;
    }
  };

  const flush = () => {
    if (flushedRef.current) return;
    if (!consentGranted()) return;
    if (EXCLUDE.some((p) => curPathRef.current.startsWith(p))) return;
    flushedRef.current = true;
    const dwell = Date.now() - startRef.current;
    send([{
      type: 'engage', path: curPathRef.current, vid: getVid(), sid: getSid(),
      device: device(), dwell_ms: dwell, scroll_pct: maxScrollRef.current,
    }]);
  };

  // Pageview on every route change; flush the previous page first.
  useEffect(() => {
    if (!consentGranted()) return;
    if (EXCLUDE.some((p) => path.startsWith(p))) return;
    // Flush the page we are leaving (if this is a soft navigation).
    if (curPathRef.current !== path) flush();
    curPathRef.current = path;
    startRef.current = Date.now();
    maxScrollRef.current = scrollPct();
    flushedRef.current = false;
    // Remember the last marketing page visited so sign-up forms (e.g. /get-started)
    // can attribute which page drove the conversion. Tab-scoped, functional only.
    try {
      if (path !== '/get-started' && !EXCLUDE.some((p) => path.startsWith(p))) {
        sessionStorage.setItem('cvna_source_page', path);
      }
    } catch { /* ignore */ }
    send([{
      type: 'pageview', path, vid: getVid(), sid: getSid(), device: device(),
      ref: document.referrer || '', title: document.title || '',
    }]);
    // eslint-disable-next-line
  }, [path]);

  // Scroll depth, clicks, and leave/hide flush — bound once.
  useEffect(() => {
    const onScroll = () => { maxScrollRef.current = Math.max(maxScrollRef.current, scrollPct()); };
    const onClick = (e) => {
      if (!consentGranted()) return;
      if (EXCLUDE.some((p) => curPathRef.current.startsWith(p))) return;
      if (e.target.closest && e.target.closest('.cookie-consent-bar')) return; // don't track consent-banner interactions
      try {
        const doc = document.documentElement;
        const x = Math.round((e.clientX / window.innerWidth) * 10000) / 100;
        const y = Math.round(((e.clientY + window.scrollY) / doc.scrollHeight) * 10000) / 100;
        const el = e.target.closest('a,button,[role="button"],summary,input,[data-testid]') || e.target;
        const txt = (el.innerText || el.getAttribute?.('aria-label') || el.getAttribute?.('data-testid') || '').trim().slice(0, 80);
        send([{
          type: 'click', path: curPathRef.current, vid: getVid(), sid: getSid(),
          device: device(), x_pct: x, y_pct: y, tag: (el.tagName || '').toLowerCase(), txt,
        }]);
      } catch {
        /* never block */
      }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    const onConsentChange = () => { if (!consentGranted()) clearVidCookie(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    window.addEventListener('cv-consent-changed', onConsentChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('cv-consent-changed', onConsentChange);
    };
    // eslint-disable-next-line
  }, []);

  return null;
}
